import type {
  AdminDashboardStats,
  AdminUserListItem,
  AdminAuditLog,
  AdminUser,
  AIProviderConfig,
  AIProviderName,
} from '@frappi/shared';

export interface AIProviderSettingsResponse {
  config: AIProviderConfig;
  availableModels: Record<AIProviderName, string[]>;
  keyStatus: { geminiKeySet: boolean; anthropicKeySet: boolean; workersAiKeySet: boolean };
}

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

class AdminApiClient {
  private token: string | null = null;

  setToken(token: string | null) {
    this.token = token;
  }

  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(this.token ? { Authorization: `Bearer ${this.token}` } : {}),
        ...options?.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error((errorData as { message?: string }).message || `Error ${response.status}`);
    }

    const json = await response.json() as { data?: T; pagination?: unknown; success: boolean };
    // Return paginated response as-is, otherwise unwrap data
    if (json.pagination) return json as unknown as T;
    return json.data !== undefined ? json.data as T : json as unknown as T;
  }

  auth = {
    login: (email: string, password: string) =>
      this.request<{ token: string; user: AdminUser }>('/api/admin/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      }),
    me: () => this.request<AdminUser>('/api/admin/auth/me'),
    logout: () => this.request<{ message: string }>('/api/admin/auth/logout', { method: 'POST' }),
    refresh: () => this.request<{ token: string; user: AdminUser; expiresAt: string }>('/api/admin/auth/refresh', { method: 'POST' }),
    updatePassword: (currentPassword: string, newPassword: string) =>
      this.request<{ message: string }>('/api/admin/auth/password', {
        method: 'PUT',
        body: JSON.stringify({ currentPassword, newPassword }),
      }),
  };

  adminUsers = {
    list: () => this.request<AdminUser[]>('/api/admin/admin-users'),
    create: (data: { email: string; password: string; name: string; role: 'admin' | 'superadmin' }) =>
      this.request<AdminUser>('/api/admin/admin-users', { method: 'POST', body: JSON.stringify(data) }),
    changeRole: (id: string, role: 'admin' | 'superadmin') =>
      this.request<void>(`/api/admin/admin-users/${id}/role`, { method: 'PUT', body: JSON.stringify({ role }) }),
    delete: (id: string) =>
      this.request<void>(`/api/admin/admin-users/${id}`, { method: 'DELETE' }),
  };

  dashboard = {
    getStats: () => this.request<AdminDashboardStats>('/api/admin/dashboard/stats'),
    getRegistrations: (days = 30) => this.request<{ date: string; count: number }[]>(`/api/admin/dashboard/charts/registrations?days=${days}`),
    getAiUsage: (days = 30) => this.request<{ date: string; scans: number; helps: number }[]>(`/api/admin/dashboard/charts/ai-usage?days=${days}`),
    getPlansDistribution: () => this.request<{ plan: string; count: number }[]>('/api/admin/dashboard/charts/plans'),
    getRecentUsers: (limit = 10) => this.request<AdminUserListItem[]>(`/api/admin/dashboard/recent-users?limit=${limit}`),
    getTopAiUsers: (limit = 10) => this.request<AdminUserListItem[]>(`/api/admin/dashboard/top-ai-users?limit=${limit}`),
    getSubscriptionKPIs: () => this.request<{
      mrr: number; activeSubscriptions: number; trialingSubscriptions: number;
      pastDueSubscriptions: number; canceledThisMonth: number;
      churnRate: number; conversionRate: number; trialsExpiringSoon: number;
    }>('/api/admin/dashboard/subscription-kpis'),
  };

  users = {
    list: (params: Record<string, string>) => {
      const query = new URLSearchParams(params).toString();
      return this.request<{ data: AdminUserListItem[]; pagination: { page: number; pageSize: number; total: number; totalPages: number } }>(`/api/admin/users?${query}`);
    },
    getDetail: async (id: string) => {
      const res = await this.request<{ user: Record<string, unknown>; subscription: Record<string, unknown> | null; usage: Record<string, unknown> | null }>(`/api/admin/users/${id}`);
      return res.user;
    },
    getSubscription: async (id: string) => {
      const res = await this.request<{ subscription: Record<string, unknown>; verifications: Record<string, unknown>[]; events: Record<string, unknown>[] } | null>(`/api/admin/users/${id}/subscription`);
      return res?.subscription ?? null;
    },
    getUsage: (id: string, months = 6) => this.request<Record<string, unknown>[]>(`/api/admin/users/${id}/usage?months=${months}`),
    getStudents: (id: string) => this.request<Record<string, unknown>[]>(`/api/admin/users/${id}/students`),
    getDevices: (id: string) => this.request<Record<string, unknown>[]>(`/api/admin/users/${id}/devices`),
    resetQuotas: (id: string) =>
      this.request<void>(`/api/admin/users/${id}/reset-quotas`, { method: 'PUT' }),
    delete: (id: string) =>
      this.request<void>(`/api/admin/users/${id}`, { method: 'DELETE' }),
  };

  subscriptions = {
    list: (params: Record<string, string>) => {
      const query = new URLSearchParams(params).toString();
      return this.request<{ data: Record<string, unknown>[]; pagination: { page: number; pageSize: number; total: number; totalPages: number } }>(`/api/admin/subscriptions?${query}`);
    },
    getDetail: (id: string) => this.request<Record<string, unknown>>(`/api/admin/subscriptions/${id}`),
    getEvents: (id: string) => this.request<Record<string, unknown>[]>(`/api/admin/subscriptions/${id}/events`),
    getVerifications: (id: string) => this.request<Record<string, unknown>[]>(`/api/admin/subscriptions/${id}/verifications`),
  };

  licenses = {
    list: (params?: Record<string, string>) => {
      const query = params ? new URLSearchParams(params).toString() : '';
      return this.request<{ data: Record<string, unknown>[]; pagination: { page: number; pageSize: number; total: number; totalPages: number } }>(`/api/admin/licenses?${query}`);
    },
    activate: (data: { email: string; planTier: string; durationMonths: number; notes?: string }) =>
      this.request<Record<string, unknown>>('/api/admin/licenses', { method: 'POST', body: JSON.stringify(data) }),
    extend: (id: string, months: number, notes?: string) =>
      this.request<void>(`/api/admin/licenses/${id}/extend`, { method: 'PUT', body: JSON.stringify({ months, notes }) }),
    revoke: (id: string) =>
      this.request<void>(`/api/admin/licenses/${id}/revoke`, { method: 'PUT' }),
  };

  audit = {
    list: (params: Record<string, string>) => {
      const query = new URLSearchParams(params).toString();
      return this.request<{ data: AdminAuditLog[]; pagination: { page: number; pageSize: number; total: number; totalPages: number } }>(`/api/admin/audit?${query}`);
    },
  };

  settings = {
    getAIProviders: () =>
      this.request<AIProviderSettingsResponse>('/api/admin/settings/ai-providers'),
    updateAIProviders: (config: AIProviderConfig) =>
      this.request<{ config: AIProviderConfig }>('/api/admin/settings/ai-providers', {
        method: 'PUT',
        body: JSON.stringify(config),
      }),
  };

  system = {
    getHealth: () => this.request<Record<string, unknown>>('/api/admin/system/health'),
    getWebhooks: (limit = 20) => this.request<Record<string, unknown>[]>(`/api/admin/system/webhooks?limit=${limit}`),
    getPushStats: () => this.request<Record<string, unknown>>('/api/admin/system/push-stats'),
    getDbStats: () => this.request<Record<string, number>>('/api/admin/system/db-stats'),
    verifySubscriptions: () => this.request<void>('/api/admin/system/verify-subscriptions', { method: 'POST' }),
    cleanupSessions: () => this.request<void>('/api/admin/system/cleanup-sessions', { method: 'POST' }),
  };
}

export const api = new AdminApiClient();
